import { FC, PropsWithChildren, useMemo, useState } from 'react';
import { RequiredKeyDataType } from '../schema';
import { AlgorithmType } from './schema/zAlgorithmType';
import { AlgorithmTargetGraphicViewModal } from './targets/AlgorithmTargetGraphicViewModal';
import { AlgorithmTargets } from './targets/AlgorithmTargets';
import { AlgorithmTargetsHeader } from './targets/AlgorithmTargetsHeader';
import { useGetAlgorithmTargetsQuery } from './targets/query/useGetAlgorithmTargetsQuery';
import { AlgorithmTargetFormDataType } from './targets/schema/types';

export interface AlgorithmTargetsViewProps extends PropsWithChildren {
  algorithmId: number;
  algorithmTypeCode?: AlgorithmType;
  requiredKeys?: RequiredKeyDataType[];
  targetId?: number;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  editGraphLabel?: string;
}

export const AlgorithmTargetsView: FC<AlgorithmTargetsViewProps> = (props) => {
  const { algorithmId } = props;
  const { data: algorithmTargets, isLoading } = useGetAlgorithmTargetsQuery({
    id: algorithmId,
  });

  if (isLoading) {
    return <AlgorithmTargetsHeader isLoading="add" onAddButtonClick={noop} />;
  }

  if (!algorithmTargets) {
    return null;
  }

  return (
    <AlgorithmTargetsViewInner {...props} targets={algorithmTargets.targets} />
  );
};

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = () => {};

interface AlgorithmTargetsViewInnerProps extends AlgorithmTargetsViewProps {
  targets: AlgorithmTargetFormDataType[];
  editGraphLabel?: string;
}

const AlgorithmTargetsViewInner: FC<AlgorithmTargetsViewInnerProps> = (
  props,
) => {
  const {
    algorithmId,
    algorithmTypeCode = AlgorithmType.INSERT,
    requiredKeys,
    expanded,
    onToggle,
    targets,
    editGraphLabel,
  } = props;
  const [targetId, setTargetId] = useState<number | undefined>(props.targetId);

  const target = useMemo(
    () => (targetId ? targets.find((t) => t.id === targetId) : undefined),
    [targets, targetId],
  );

  return (
    <>
      <AlgorithmTargets
        algorithmId={algorithmId}
        requiredKeys={requiredKeys}
        targetId={targetId !== undefined ? Number(targetId) : undefined}
        expanded={expanded}
        onToggle={onToggle}
        onGraphButtonClick={(id) => setTargetId(id)}
        editGraphLabel={editGraphLabel}
      />
      {target && (
        <AlgorithmTargetGraphicViewModal
          algorithmId={algorithmId}
          algorithmTypeCode={algorithmTypeCode}
          target={target}
          onClose={() => setTargetId(undefined)}
        />
      )}
    </>
  );
};
